import { POSTS } from "virtual:warike-posts";
import type { PostFile } from "virtual:warike-posts";

// The site's posts and tags, read from the manifest (D1254). Newest first; a tag's slug is its address.

export type ListedPost = { readonly slug: string; readonly file: PostFile };

export const BLOG_PATH = "/blog";
export const postPath = (slug: string) => `${BLOG_PATH}/${slug}`;
export const tagSlug = (tag: string) => tag.toLowerCase().replace(/[^a-z0-9]+/g, "-");
export const tagPath = (tag: string) => `${BLOG_PATH}/tag/${tagSlug(tag)}`;

export const LISTED_POSTS: readonly ListedPost[] = POSTS.flatMap((post) =>
  post.languages.en ? [{ slug: post.slug, file: post.languages.en }] : [],
).toSorted((a, b) => b.file.publishedAt.localeCompare(a.file.publishedAt));

/** When a post last changed, as its own page and every list of it say. */
export const postChanged = (file: PostFile) => file.updatedAt ?? file.publishedAt;

/** When a list of posts last changed: its newest post. No post, no date (D1347). */
export const newestPostDate = (posts: readonly ListedPost[]) =>
  posts
    .map(({ file }) => postChanged(file))
    .toSorted()
    .at(-1) ?? null;

export const postsWithTag = (tag: string) => LISTED_POSTS.filter((post) => post.file.tags.includes(tag));

/** Every tag with its count: most used first, then by name, as warike.tech orders them. */
export const ALL_TAGS = [...new Set(LISTED_POSTS.flatMap((post) => post.file.tags))]
  .map((name) => ({ name, count: postsWithTag(name).length }))
  .toSorted((a, b) => b.count - a.count || a.name.localeCompare(b.name));

/** The post beside this one: `previous` is older, `next` newer. */
export function neighbours(slug: string) {
  const at = LISTED_POSTS.findIndex((post) => post.slug === slug);
  return { next: at > 0 ? LISTED_POSTS[at - 1] : undefined, previous: LISTED_POSTS[at + 1] };
}

/** A date as a reader sees it: never by their clock, or hydration would mismatch across midnight. */
export const postDate = (iso: string) =>
  new Intl.DateTimeFormat("en", { dateStyle: "long", timeZone: "UTC" }).format(new Date(iso));
